<template>
  <!-- 차트 블록 — vue-echarts. bar/line/pie. takeaway 1줄 + primary 단일 hue 그라디언트 -->
  <div class="ab-chart">
    <div v-if="data.takeaway" class="ab-chart__takeaway">{{ data.takeaway }}</div>
    <div class="ab-chart__frame">
      <div v-if="data.title" class="ab-chart__title">{{ data.title }}</div>
      <v-chart
        v-if="hasData"
        class="ab-chart__canvas"
        :style="{ height: chartHeight + 'px' }"
        :option="option"
        autoresize
      />
      <div v-else class="ab-chart__empty">{{ $t('assistant.chartEmpty') }}</div>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components'
import VChart from 'vue-echarts'

use([CanvasRenderer, BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent])

/** primary 토큰 그라디언트 폴백 (variables.css --color-primary-300~700 계열) */
const PRIMARY_RAMP_FALLBACK = ['#7E96C2', '#5C77AD', '#3B5998', '#2C4578', '#1E3058']

/**
 * variables.css 의 primary 토큰을 런타임 추출해 단일 hue 그라디언트 ramp 생성
 * (PRODUCT_UX §2.5.3 — primary 단일 hue, 하드코딩 색 금지 원칙의 토큰 추출 방식).
 * @returns {string[]} echarts color 배열
 */
function primaryRamp() {
  try {
    const styles = getComputedStyle(document.documentElement)
    const ramp = ['--color-primary-300', '--color-primary-400', '--color-primary-500', '--color-primary-600', '--color-primary-700']
      .map((t) => styles.getPropertyValue(t).trim())
      .filter(Boolean)
    return ramp.length >= 3 ? ramp : PRIMARY_RAMP_FALLBACK
  } catch (e) {
    return PRIMARY_RAMP_FALLBACK
  }
}

/**
 * @component ChartBlock
 * @description data={chartType:'bar'|'line'|'pie', labels:[], series:[{name,data:[]}], title?, takeaway?} 차트.
 *   takeaway 는 차트 위 1줄 핵심 요약(PRODUCT_UX §2.5.3). 색은 primary 토큰 그라디언트.
 */
export default {
  name: 'ChartBlock',
  components: { VChart },
  props: {
    data: { type: Object, default: () => ({}) }
  },
  computed: {
    labels() { return Array.isArray(this.data.labels) ? this.data.labels : [] },
    // malformed payload(null/문자열 원소, data 비배열) 방어: 유효 series만 정규화
    series() {
      return (Array.isArray(this.data.series) ? this.data.series : [])
        .filter((s) => s && typeof s === 'object' && Array.isArray(s.data))
        .map((s) => ({ name: s.name || '', data: s.data }))
    },
    hasData() { return this.labels.length > 0 && this.series.length > 0 },
    chartType() {
      return ['bar', 'line', 'pie'].includes(this.data.chartType) ? this.data.chartType : 'bar'
    },
    /** 라벨 수가 많으면 높이 확장(회전 라벨 공간), pie 는 고정 */
    chartHeight() {
      if (this.chartType === 'pie') return 240
      return this.labels.length > 8 ? 280 : 220
    },
    option() {
      const colors = primaryRamp()
      if (this.chartType === 'pie') {
        const s = this.series[0] || { data: [] }
        return {
          color: colors,
          tooltip: { trigger: 'item' },
          legend: { bottom: 0, type: 'scroll' },
          series: [{
            type: 'pie',
            radius: '60%',
            data: this.labels.map((l, i) => ({ name: l, value: (s.data || [])[i] }))
          }]
        }
      }
      return {
        color: colors.slice(2).concat(colors.slice(0, 2)), // 첫 시리즈 = primary-500
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, type: 'scroll' },
        grid: { left: 40, right: 16, top: 16, bottom: 40 },
        xAxis: {
          type: 'category',
          data: this.labels,
          axisLabel: this.labels.length > 8 ? { rotate: 35, fontSize: 10 } : {}
        },
        yAxis: { type: 'value' },
        series: this.series.map((s) => ({ name: s.name, type: this.chartType, data: s.data || [] }))
      }
    }
  }
}
</script>

<style scoped>
.ab-chart { width: 100%; }
/* 차트 위 1줄 핵심 요약 (PRODUCT_UX §2.5.3) */
.ab-chart__takeaway {
  font-size: 13px;
  color: var(--color-gray-800, #1E2228);
  margin-bottom: 6px;
  line-height: 1.5;
}
/* 차트 wrapper — white bg + hairline + radius (PRODUCT_UX §2.5.3) */
.ab-chart__frame {
  background: var(--color-white, #fff);
  border: 1px solid var(--color-gray-200, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  padding: 10px 8px 6px;
}
.ab-chart__title { font-size: 12.5px; font-weight: 600; color: var(--color-gray-600, #5f6368); margin: 0 4px 4px; }
.ab-chart__canvas { width: 100%; }
/* 빈 데이터 chip — 도메인 공통 "결과 없음" 톤 (PRODUCT_UX §2.5.8) */
.ab-chart__empty {
  display: inline-block;
  margin: 12px auto;
  padding: 4px 12px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-gray-100, #E8EAED);
  color: var(--color-gray-600, #5f6368);
  font-size: 12.5px;
}
</style>
